/* ============================================================
   LUXE THEME — Interaction engine
   No dependencies. Progressive enhancement only.
   ============================================================ */
(function () {
  'use strict';

  /* ---------- Page-load entrance ---------- */
  function initLoad() {
    function reveal() {
      requestAnimationFrame(function () {
        document.body.classList.add('is-loaded');
      });
    }
    if (document.readyState === 'complete') setTimeout(reveal, 60);
    else window.addEventListener('load', function () { setTimeout(reveal, 60); });
    // safety fallback so content never stays hidden
    setTimeout(function () { document.body.classList.add('is-loaded'); }, 2500);
  }

  /* ---------- Parallax ---------- */
  function initParallax() {
    var els = Array.prototype.slice.call(document.querySelectorAll('[data-parallax]'));
    if (!els.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var ticking = false;
    function update() {
      var vh = window.innerHeight;
      els.forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;
        var speed = parseFloat(el.dataset.parallax) || 0.12;
        var offset = (r.top + r.height / 2 - vh / 2) * -speed;
        el.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
      });
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ---------- Scroll cue ---------- */
  function initScrollCue() {
    document.querySelectorAll('.luxe-hero__scroll').forEach(function (cue) {
      cue.style.cursor = 'pointer';
      cue.addEventListener('click', function () {
        var hero = cue.closest('.luxe-hero');
        var next = hero && hero.nextElementSibling;
        if (next) next.scrollIntoView({ behavior: 'smooth', block: 'start' });
        else window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
      });
    });
  }

  /* ---------- Cascade stagger for grids/tiles/carousels ---------- */
  function applyStagger() {
    document.querySelectorAll('.luxe-grid, .luxe-tiles__grid, .luxe-carousel__track').forEach(function (c) {
      Array.prototype.forEach.call(c.children, function (child, i) {
        var t = child.matches && child.matches('.luxe-reveal, .luxe-rise, .luxe-reveal--mask')
          ? child : (child.querySelector ? child.querySelector('.luxe-reveal, .luxe-rise, .luxe-reveal--mask') : null);
        if (t) t.style.transitionDelay = Math.min(i, 8) * 70 + 'ms';
      });
    });
  }

  /* ---------- Scroll reveal ---------- */
  function initReveal() {
    var sel = '.luxe-reveal, .luxe-rise, .luxe-reveal--mask';
    var els = document.querySelectorAll(sel);
    if (!('IntersectionObserver' in window) || !els.length) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -6% 0px' });
    els.forEach(function (el) { io.observe(el); });
    // safety: anything still hidden after 3.5s reveals (prevents stuck sections)
    setTimeout(function () { els.forEach(function (el) { el.classList.add('is-in'); }); }, 3500);
  }

  /* ---------- Sticky / transparent header ---------- */
  function initHeader() {
    var header = document.querySelector('.luxe-header');
    if (!header) return;
    var hideOnScroll = header.dataset.hideOnScroll === 'true';
    var lastY = window.scrollY;
    function onScroll() {
      var y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 40);
      if (hideOnScroll) {
        if (y > lastY && y > 240) header.classList.add('is-hidden');
        else header.classList.remove('is-hidden');
      }
      lastY = y;
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- Generic drawer (nav + cart) ---------- */
  function openPanel(panel) {
    panel.classList.add('is-open');
    document.body.classList.add('luxe-no-scroll');
    var focusable = panel.querySelector('[autofocus],button,a,input');
    if (focusable) setTimeout(function () { focusable.focus(); }, 60);
  }
  function closePanel(panel) {
    panel.classList.remove('is-open');
    if (!document.querySelector('.luxe-drawer.is-open,.luxe-cart.is-open')) {
      document.body.classList.remove('luxe-no-scroll');
    }
  }
  function initPanels() {
    document.addEventListener('click', function (e) {
      var openBtn = e.target.closest('[data-panel-open]');
      if (openBtn) {
        var p = document.getElementById(openBtn.dataset.panelOpen);
        if (p) { e.preventDefault(); openPanel(p); }
      }
      var closeBtn = e.target.closest('[data-panel-close]');
      if (closeBtn) {
        var panel = closeBtn.closest('.luxe-drawer,.luxe-cart');
        if (panel) { e.preventDefault(); closePanel(panel); }
      }
      if (e.target.classList.contains('luxe-drawer__overlay') || e.target.classList.contains('luxe-cart__overlay')) {
        closePanel(e.target.closest('.luxe-drawer,.luxe-cart'));
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        document.querySelectorAll('.luxe-drawer.is-open,.luxe-cart.is-open').forEach(closePanel);
      }
    });
  }

  /* ---------- Mobile nav accordion ---------- */
  function initNavAccordion() {
    document.querySelectorAll('[data-nav-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        btn.closest('.luxe-nav__item').classList.toggle('is-expanded');
      });
    });
  }

  /* ---------- Accordion sections (PDP) ---------- */
  function initAccordions() {
    document.querySelectorAll('[data-accordion] [data-accordion-trigger]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var item = btn.closest('[data-accordion-item]');
        item.classList.toggle('is-open');
        var body = item.querySelector('[data-accordion-body]');
        if (body) body.style.maxHeight = item.classList.contains('is-open') ? body.scrollHeight + 'px' : '0px';
      });
    });
  }

  /* ---------- Carousel ---------- */
  function initCarousels() {
    document.querySelectorAll('.luxe-carousel').forEach(function (c) {
      var track = c.querySelector('.luxe-carousel__track');
      var prev = c.querySelector('.luxe-carousel__nav--prev');
      var next = c.querySelector('.luxe-carousel__nav--next');
      if (!track) return;
      function step() { return track.firstElementChild ? track.firstElementChild.offsetWidth + 1 : 320; }
      function update() {
        if (!prev || !next) return;
        prev.disabled = track.scrollLeft <= 4;
        next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      }
      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
      track.addEventListener('scroll', update, { passive: true });
      update();
    });
  }

  /* ---------- Hero video play / pause ---------- */
  function initHero() {
    document.querySelectorAll('.luxe-hero__playpause').forEach(function (btn) {
      var video = btn.closest('.luxe-hero').querySelector('video');
      if (!video) { btn.hidden = true; return; }
      btn.addEventListener('click', function () {
        if (video.paused) { video.play(); btn.dataset.state = 'playing'; }
        else { video.pause(); btn.dataset.state = 'paused'; }
        btn.querySelectorAll('[data-pp]').forEach(function (i) { i.hidden = !i.hidden; });
      });
    });
  }

  /* ---------- Wishlist (localStorage, rich) ---------- */
  function initWishlist() {
    var KEY = 'luxe_wishlist_v2';
    function get() { try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) { return []; } }
    function set(v) { localStorage.setItem(KEY, JSON.stringify(v)); }
    function paintButtons() {
      var ids = get().map(function (i) { return String(i.id); });
      document.querySelectorAll('[data-wishlist]').forEach(function (b) {
        b.classList.toggle('is-active', ids.indexOf(String(b.dataset.wishlist)) > -1);
      });
      var count = document.querySelectorAll('[data-wishlist-count]');
      count.forEach(function (c) { c.textContent = ids.length; c.hidden = ids.length === 0; });
    }
    function renderPage() {
      var grids = document.querySelectorAll('[data-wishlist-grid]');
      var empties = document.querySelectorAll('[data-wishlist-empty]');
      var items = get();
      empties.forEach(function (e) { e.hidden = items.length > 0; });
      if (!grids.length) return;
      var html = items.map(function (i) {
        return '<article class="luxe-card"><div class="luxe-card__frame"><a class="luxe-card__media" href="' + i.url + '">' +
          (i.image ? '<img class="luxe-card__img luxe-card__img--main" src="' + i.image + '" alt="" loading="lazy">' : '') +
          '</a><button class="luxe-card__wish is-active" data-wishlist="' + i.id + '" aria-label="Remove">' +
          '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.25"><path d="M6 3.5h12a1 1 0 0 1 1 1v16l-7-4-7 4v-16a1 1 0 0 1 1-1Z"/></svg></button></div>' +
          '<div class="luxe-card__info"><h3 class="luxe-card__title"><a href="' + i.url + '">' + i.title + '</a></h3>' +
          '<div class="luxe-card__price">' + (i.price || '') + '</div></div></article>';
      }).join('');
      grids.forEach(function (g) { g.innerHTML = html; });
    }
    document.addEventListener('click', function (e) {
      var b = e.target.closest('[data-wishlist]');
      if (!b) return;
      e.preventDefault();
      var list = get();
      var id = String(b.dataset.wishlist);
      var idx = list.map(function (i) { return String(i.id); }).indexOf(id);
      if (idx > -1) list.splice(idx, 1);
      else list.push({ id: id, handle: b.dataset.wishlistHandle, title: b.dataset.wishlistTitle, url: b.dataset.wishlistUrl, image: b.dataset.wishlistImage, price: b.dataset.wishlistPrice });
      set(list); paintButtons(); renderPage();
    });
    paintButtons(); renderPage();
  }

  /* ---------- AJAX cart (Section Rendering API) ---------- */
  function setCounts(n) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = n; el.hidden = n === 0;
    });
  }
  function swapDrawer(sections) {
    if (!sections || !sections['cart-drawer']) return;
    var tmp = document.createElement('div');
    tmp.innerHTML = sections['cart-drawer'];
    var fresh = tmp.querySelector('[data-cart-contents]');
    var live = document.querySelector('#luxe-cart [data-cart-contents]');
    if (fresh && live) live.innerHTML = fresh.innerHTML;
  }
  function openCartDrawer() {
    var d = document.getElementById('luxe-cart');
    if (d) openPanel(d);
  }
  function refreshCount() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { setCounts(cart.item_count); return cart; })
      .catch(function () {});
  }
  function initCart() {
    // Add to cart (product form + quick-add)
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('form[action$="/cart/add"], form[data-luxe-add]');
      if (!form) return;
      e.preventDefault();
      var btn = form.querySelector('[type="submit"]');
      if (btn) { btn.setAttribute('aria-busy', 'true'); btn.dataset.label = btn.textContent; }
      var fd = new FormData(form);
      fd.append('sections', 'cart-drawer');
      fd.append('sections_url', window.location.pathname);
      fetch('/cart/add.js', { method: 'POST', headers: { 'Accept': 'application/json' }, body: fd })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data.status) { // error (e.g. sold out)
            if (btn) btn.textContent = data.description || 'Unavailable';
            return;
          }
          swapDrawer(data.sections);
          return refreshCount().then(openCartDrawer);
        })
        .finally(function () { if (btn) { btn.removeAttribute('aria-busy'); } });
    });

    // Quantity change / remove
    document.addEventListener('click', function (e) {
      var q = e.target.closest('[data-cart-change]');
      if (!q) return;
      e.preventDefault();
      q.setAttribute('aria-busy', 'true');
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: q.dataset.key, quantity: parseInt(q.dataset.cartChange, 10), sections: 'cart-drawer', sections_url: window.location.pathname })
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          swapDrawer(cart.sections);
          setCounts(cart.item_count);
          // keep cart page in sync if present
          if (document.querySelector('.luxe-cartpage')) window.location.reload();
        });
    });

    refreshCount();
  }

  /* ---------- Footer accordion (mobile) ---------- */
  function initFooter() {
    document.querySelectorAll('[data-footer-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (window.matchMedia('(min-width:750px)').matches) return;
        var col = btn.closest('.luxe-footer__col');
        var open = col.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }

  function boot() {
    initLoad(); applyStagger(); initReveal(); initParallax(); initScrollCue();
    initHeader(); initPanels(); initNavAccordion();
    initAccordions(); initCarousels(); initHero(); initWishlist(); initCart(); initFooter();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
